<template>
  <div class="exclusions-duree">
    <div class="exclusions-entete">
      <div>
        <h4 class="mb-1">Exclusions après la durée de couverture</h4>
        <p class="text-grey mb-0">Prestations qui ne sont plus couvertes quand l’hospitalisation dépasse la durée de couverture du contrat
          (réglée dans « Facturation »). La liste vaut pour toutes les assurances et conventions.</p>
      </div>
      <button type="button" class="btn btn-primary btn-radius" @click="ouvrir(null)">Modifier les exclusions</button>
    </div>

    <div v-if="erreur && !fenetre" class="alert alert-danger" role="alert">{{ erreur }}</div>
    <div v-if="chargement" class="text-grey">Chargement des exclusions…</div>
    <div v-else-if="!groupes.length" class="exclusions-vide">Aucune exclusion après la durée de couverture.</div>
    <div v-else>
      <div v-for="groupe in groupes" :key="groupe.type" class="exclusions-groupe">
        <div class="exclusions-groupe-entete">
          <h5 class="mb-0">{{ groupe.libelle }} <span class="exclusions-compteur">{{ groupe.prestations.length }}</span></h5>
          <button type="button" class="btn btn-link" @click="ouvrir(groupe.type)">Modifier</button>
        </div>
        <div class="exclusions-puces">
          <span v-for="prestation in groupe.prestations" :key="prestation.prestation_id" class="exclusions-puce">{{ prestation.libelle }}</span>
        </div>
      </div>
    </div>

    <div v-if="fenetre" class="exclusions-fenetre" role="dialog" aria-modal="true" aria-labelledby="exclusions-titre">
      <form class="exclusions-fenetre-contenu" novalidate @submit.prevent="enregistrer">
        <div class="exclusions-fenetre-entete">
          <h4 id="exclusions-titre" class="mb-0">Prestations exclues après la durée de couverture</h4>
          <button type="button" class="btn btn-link exclusions-fermer" aria-label="Fermer" @click="fermer">×</button>
        </div>
        <div class="exclusions-outils">
          <div class="form-group mb-0"><label for="exclusions-type">Type de prestation</label>
            <select id="exclusions-type" v-model="fenetre.type" class="input-secondary-light" @change="changerType">
              <option v-for="type in types" :key="type.type" :value="type.type">{{ type.libelle }}</option>
            </select></div>
          <div class="form-group mb-0 exclusions-recherche"><label for="exclusions-filtre">Rechercher</label>
            <input id="exclusions-filtre" v-model.trim="fenetre.filtre" type="search" class="input-secondary-light" placeholder="Nom de la prestation"></div>
        </div>
        <div v-if="fenetre.chargement" class="text-grey">Chargement des prestations…</div>
        <div v-else-if="fenetre.erreur" class="text-danger">{{ fenetre.erreur }}</div>
        <template v-else>
          <div class="exclusions-tout">
            <label><input type="checkbox" :checked="toutCoche" @change="toutCocher($event.target.checked)"> Tout sélectionner</label>
            <span class="text-grey">{{ fenetre.coches.length }} sélectionnée(s)</span>
          </div>
          <div v-if="!groupesFiltres.length" class="text-grey">Aucune prestation.</div>
          <div v-for="(groupe, index) in groupesFiltres" :key="groupe.titre + index" class="exclusions-categorie">
            <label v-if="groupe.titre" class="exclusions-categorie-titre">
              <input type="checkbox" :checked="groupeCoche(groupe)" @change="cocherGroupe(groupe, $event.target.checked)"> {{ groupe.titre }}</label>
            <div class="exclusions-items">
              <label v-for="item in groupe.items" :key="item.id"><input type="checkbox" :checked="estCoche(item)" @change="basculer(item, $event.target.checked)"> {{ item.libelle }}</label>
            </div>
          </div>
        </template>
        <p v-if="erreur" class="text-danger" role="alert">{{ erreur }}</p>
        <div class="text-right mt-3">
          <button type="button" class="btn btn-secondary-light-o btn-radius" @click="fermer">Annuler</button>
          <button type="submit" class="btn btn-primary btn-radius ml-2" :disabled="enregistrement || fenetre.chargement">{{ enregistrement ? 'Enregistrement…' : 'Enregistrer' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ExclusionsApi from '@/api/parametres/_exclusions-duree'

// Mêmes types et mêmes identifiants que Caisse › Paramètres › Exclusions (be_caisse checkIfPrestationIsExcluded).
const TYPES = [
  { type: 'analyse', libelle: 'Analyses' },
  { type: 'examen', libelle: 'Examens' },
  { type: 'medicament', libelle: 'Médicaments' },
  { type: 'consommable', libelle: 'Consommables' },
  { type: 'chirurgie', libelle: 'Chirurgies' },
  { type: 'bilan_sante', libelle: 'Bilan de santé' },
  { type: 'acte', libelle: 'Actes' },
  { type: 'chambre', libelle: 'Chambres' },
  { type: 'observation', libelle: 'Observations' },
  { type: 'admission', libelle: 'Admissions' },
]

const liste = reponse => (Array.isArray(reponse) ? reponse : [])
const seul = items => [{ titre: '', items }]

// Chaque type : la même source que la liste correspondante de la Caisse, ramenée à [{ titre, items: [{ id, libelle }] }].
const SOURCES = {
  analyse: () => ExclusionsApi.analyses().then(r => liste(r).map(c => ({ titre: c.nom, items: liste(c.analyses).map(a => ({ id: a.id, libelle: a.nom })) }))),
  examen: () => ExclusionsApi.examens().then(r => seul(liste(r).map(e => ({ id: e.id, libelle: e.nom })))),
  medicament: () => ExclusionsApi.medicaments().then(r => liste(r).map(c => ({ titre: c.categorie, items: liste(c.medicaments).map(m => ({ id: m.medicament_id, libelle: m.medicament })) }))),
  consommable: () => ExclusionsApi.consommables().then(r => seul(liste(r).map(c => ({ id: c.id, libelle: c.libelle })))),
  chirurgie: () => ExclusionsApi.chirurgies().then(r => liste(r).map(c => ({ titre: c.chirurgie, items: liste(c.kchirurgies).map(k => ({ id: k.id, libelle: k.k_chirurgie })) }))),
  bilan_sante: () => ExclusionsApi.bilansSante().then(r => seul(liste(r).map(b => ({ id: b.id, libelle: b.libelle })))),
  acte: () => ExclusionsApi.actes().then(r => seul(liste(r).map(a => ({ id: a.id, libelle: a.acte })))),
  chambre: () => ExclusionsApi.typesAdmission('chambre').then(r => seul(liste(r).map(p => ({ id: p.id, libelle: p.nom_prestation })))),
  observation: () => ExclusionsApi.typesAdmission('observation').then(r => seul(liste(r).map(p => ({ id: p.id, libelle: p.nom_prestation })))),
  admission: () => ExclusionsApi.typesAdmission('admission').then(r => seul(liste(r).map(p => ({ id: p.id, libelle: p.nom_prestation })))),
}

function normaliser(texte) {
  return String(texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

// Exclusions après la durée de couverture (04/10/2026), déplacées de Caisse › Paramètres › Exclusions.
export default {
  name: 'exclusions-duree',
  data() {
    return { types: TYPES, exclusions: [], chargement: true, erreur: '', fenetre: null, enregistrement: false }
  },
  computed: {
    groupes() {
      const connus = TYPES.map(t => t.type)
      const autres = this.exclusions.map(e => e.type_prestation).filter((t, i, tous) => connus.indexOf(t) < 0 && tous.indexOf(t) === i)
      return TYPES.concat(autres.map(t => ({ type: t, libelle: t })))
        .map(t => ({ type: t.type, libelle: t.libelle, prestations: this.exclusions.filter(e => e.type_prestation === t.type) }))
        .filter(g => g.prestations.length)
    },
    groupesFiltres() {
      if (!this.fenetre) return []
      const mot = normaliser(this.fenetre.filtre)
      return this.fenetre.groupes.map(g => ({ titre: g.titre, items: mot ? g.items.filter(i => normaliser(i.libelle).indexOf(mot) >= 0) : g.items }))
        .filter(g => g.items.length)
    },
    elementsVisibles() {
      return this.groupesFiltres.reduce((tous, g) => tous.concat(g.items), [])
    },
    toutCoche() {
      return this.elementsVisibles.length > 0 && this.elementsVisibles.every(item => this.estCoche(item))
    },
  },
  mounted() {
    this.charger()
  },
  methods: {
    charger() {
      this.chargement = true
      return ExclusionsApi.liste().then(reponse => {
        this.exclusions = liste(reponse)
        this.erreur = ''
      }).catch(() => {
        this.erreur = 'Les exclusions n’ont pas pu être chargées.'
      }).then(() => {
        this.chargement = false
      })
    },
    ouvrir(type) {
      this.erreur = ''
      this.fenetre = { type: type || 'analyse', filtre: '', groupes: [], coches: [], chargement: true, erreur: '' }
      return this.changerType()
    },
    changerType() {
      const f = this.fenetre
      // Déjà exclues pour ce type : cochées d'avance (l'enregistrement remplace la liste de ce type, comme dans la Caisse).
      f.coches = this.exclusions.filter(e => e.type_prestation === f.type).map(e => ({ id: e.prestation_id, libelle: e.libelle }))
      f.groupes = []
      f.erreur = ''
      f.chargement = true
      const type = f.type
      return SOURCES[type]().then(groupes => {
        if (!this.fenetre || this.fenetre.type !== type) return
        this.fenetre.groupes = groupes
      }).catch(() => {
        if (this.fenetre && this.fenetre.type === type) this.fenetre.erreur = 'La liste des prestations n’a pas pu être chargée.'
      }).then(() => {
        if (this.fenetre && this.fenetre.type === type) this.fenetre.chargement = false
      })
    },
    estCoche(item) {
      return this.fenetre.coches.some(c => String(c.id) === String(item.id))
    },
    basculer(item, coche) {
      const f = this.fenetre
      f.coches = f.coches.filter(c => String(c.id) !== String(item.id))
      if (coche) f.coches.push({ id: item.id, libelle: item.libelle })
    },
    groupeCoche(groupe) {
      return groupe.items.length > 0 && groupe.items.every(item => this.estCoche(item))
    },
    cocherGroupe(groupe, coche) {
      groupe.items.forEach(item => this.basculer(item, coche))
    },
    toutCocher(coche) {
      this.elementsVisibles.forEach(item => this.basculer(item, coche))
    },
    fermer() {
      this.fenetre = null
      this.erreur = ''
    },
    enregistrer() {
      const info = this.$store ? this.$store.getters['authInfo'] : null
      this.enregistrement = true
      this.erreur = ''
      return ExclusionsApi.enregistrer({
        exclusions: this.fenetre.coches.map(c => ({ id: c.id, libelle: c.libelle })),
        data_type: this.fenetre.type,
        user_id: info ? info.id : 0,
        user: info ? info.name : '',
      }).then(reponse => {
        if (reponse && reponse.success) {
          this.fenetre = null
          if (this.$toasted) this.$toasted.show(reponse.message || 'Enregistrement effectué', { duration: 3000, theme: 'toasted-primary', type: 'success', position: 'top-center' })
          return this.charger()
        }
        this.erreur = (reponse && reponse.message) || 'L’enregistrement a échoué.'
      }).catch(() => {
        this.erreur = 'Une erreur est survenue lors de l’enregistrement. Veuillez réessayer plus tard.'
      }).then(() => {
        this.enregistrement = false
      })
    },
  },
}
</script>

<style scoped>
.exclusions-entete { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px; flex-wrap: wrap }
.exclusions-entete p { max-width: 720px }
.exclusions-vide { text-align: center; padding: 24px 0; color: var(--ui-text-secondary, #65728b) }
.exclusions-groupe { border: 1px solid #edf0f6; border-radius: 14px; padding: 14px 16px; margin-bottom: 12px }
.exclusions-groupe-entete { display: flex; justify-content: space-between; align-items: center }
.exclusions-compteur { display: inline-block; min-width: 24px; padding: 1px 8px; border-radius: 999px; background: #e5f1ff; color: #0075ff; font-size: 13px; text-align: center }
.exclusions-puces { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px }
.exclusions-puce { padding: 4px 10px; border-radius: 999px; background: #f3f6fc; color: var(--ui-text, #304269); font-size: 14px }
.exclusions-fenetre { position: fixed; z-index: 1050; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(30, 40, 64, .45); padding: 16px }
.exclusions-fenetre-contenu { background: #fff; border-radius: 16px; width: 100%; max-width: 980px; max-height: 90vh; overflow-y: auto; padding: 24px }
.exclusions-fenetre-entete { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px }
.exclusions-fenetre-entete h4 { font-size: 22px; line-height: 1.3 }
.exclusions-fermer { font-size: 24px; line-height: 1 }
.exclusions-outils { display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap }
.exclusions-outils .form-group label { display: block }
.exclusions-outils select { min-width: 220px }
.exclusions-recherche { flex: 1; min-width: 220px }
.exclusions-tout { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #edf0f6; margin-bottom: 8px }
.exclusions-fenetre-contenu label { display: inline-flex; align-items: center; gap: 6px; margin: 0 }
.exclusions-categorie { margin: 12px 0 }
.exclusions-categorie-titre { font-weight: 700; color: #0075ff; margin-bottom: 6px !important }
.exclusions-items { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 6px 16px; padding-left: 4px }
</style>
